import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import DataTable from "../../components/DataTable";
import Modal from "../../components/Modal";
import Alert from "../../components/Alert";
import { listUsers, createUser, updateUser, deleteUser } from "../../api/userApi";
import { listRoles } from "../../api/roleApi";
import { listStaff } from "../../api/staffApi";
import "./UsersPage.css";

const emptyCreateForm = { userName: "", password: "", nickName: "", mobileNo: "", roleId: "", staffId: "", superAdmin: false };
const emptyEditForm = { nickName: "", mobileNo: "", roleId: "", superAdmin: false, enabled: true };

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [modalMode, setModalMode] = useState(null); // 'create' | 'edit' | null
  const [editingUserId, setEditingUserId] = useState(null);
  const [form, setForm] = useState(emptyCreateForm);
  const [submitting, setSubmitting] = useState(false);

  const loadAll = () => {
    setLoading(true);
    Promise.all([listUsers(), listRoles(), listStaff()])
      .then(([userList, roleList, staffData]) => {
        setUsers(userList);
        setRoles(roleList);
        setStaffList(staffData);
      })
      .catch(() => setError("Failed to load users."))
      .finally(() => setLoading(false));
  };

  useEffect(loadAll, []);

  const openCreate = () => {
    setForm(emptyCreateForm);
    setModalMode("create");
  };

  const openEdit = (user) => {
    setEditingUserId(user.id);
    const role = roles.find((r) => r.roleName === user.roleName);
    setForm({
      nickName: user.nickName || "",
      mobileNo: user.mobileNo || "",
      roleId: role ? role.roleId : "",
      superAdmin: user.superAdmin,
      enabled: user.enabled,
    });
    setModalMode("edit");
  };

  const closeModal = () => {
    setModalMode(null);
    setEditingUserId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      if (modalMode === "create") {
        await createUser({ ...form, roleId: Number(form.roleId), staffId: form.staffId ? Number(form.staffId) : null });
        setSuccess("User created.");
      } else {
        await updateUser(editingUserId, { ...form, roleId: Number(form.roleId) });
        setSuccess("User updated.");
      }
      closeModal();
      loadAll();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Delete user "${user.username}"?`)) {
      return;
    }
    setError("");
    try {
      await deleteUser(user.id);
      setSuccess("User deleted.");
      loadAll();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to delete user.");
    }
  };

  const columns = [
    { key: "username", label: "Username" },
    { key: "nickName", label: "Name" },
    { key: "mobileNo", label: "Mobile" },
    { key: "roleName", label: "Role" },
    { key: "staffCustomId", label: "HR Id" },
    { key: "designation", label: "Designation" },
    { key: "status", label: "Status" },
  ];

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Users</h1>
        <button type="button" className="btn-primary" onClick={openCreate}>
          + Add User
        </button>
      </div>

      <Alert type="error" onDismiss={() => setError("")}>
        {error}
      </Alert>
      <Alert type="success" onDismiss={() => setSuccess("")}>
        {success}
      </Alert>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <DataTable
          columns={columns}
          rows={users}
          actions={(user) => (
            <>
              <button type="button" className="btn-link" onClick={() => openEdit(user)}>
                Edit
              </button>
              <button type="button" className="btn-link danger" onClick={() => handleDelete(user)}>
                Delete
              </button>
            </>
          )}
        />
      )}

      {modalMode && (
        <Modal title={modalMode === "create" ? "Add User" : "Edit User"} onClose={closeModal}>
          <form onSubmit={handleSubmit} className="user-form">
            {modalMode === "create" && (
              <>
                <label>Staff (HR)</label>
                <select value={form.staffId} onChange={(e) => setForm({ ...form, staffId: e.target.value })}>
                  <option value="">Select HR</option>
                  {staffList.map((s) => (
                    <option key={s.staffId} value={s.staffId}>
                      {s.staffName}
                    </option>
                  ))}
                </select>
                <label>Username</label>
                <input
                  value={form.userName}
                  onChange={(e) => setForm({ ...form, userName: e.target.value })}
                  required
                />
                <label>Password</label>
                <input
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  required
                />
              </>
            )}
            <label>Name</label>
            <input value={form.nickName} onChange={(e) => setForm({ ...form, nickName: e.target.value })} required />

            <label>Mobile</label>
            <input value={form.mobileNo} onChange={(e) => setForm({ ...form, mobileNo: e.target.value })} />

            <label>Role</label>
            <select value={form.roleId} onChange={(e) => setForm({ ...form, roleId: e.target.value })} required>
              <option value="" disabled>
                Select a role
              </option>
              {roles.map((role) => (
                <option key={role.roleId} value={role.roleId}>
                  {role.roleName}
                </option>
              ))}
            </select>

            {modalMode === "edit" && (
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={form.enabled}
                  onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                />
                Enabled
              </label>
            )}

            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={form.superAdmin}
                onChange={(e) => setForm({ ...form, superAdmin: e.target.checked })}
              />
              Super Admin
            </label>

            <div className="form-actions">
              <button type="button" className="btn-secondary" onClick={closeModal}>
                Cancel
              </button>
              <button type="submit" className="btn-primary" disabled={submitting}>
                {submitting ? "Saving..." : "Save"}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </MainLayout>
  );
}
